import { getPortalBaseUrl } from '@/constants/constants';
import { from, lastValueFrom, map, mergeMap, toArray } from 'rxjs';
import { DropdownItem } from '../library/components/dropdown.component';
import { GuestRecord } from '../types/sharing';
import { fetchData } from './fetch-service';
import generateGuid from './guid.service';
import { getAccessToken } from './token.service';
import { getFolderUserPermissions } from './folder.service';
import { hasPermission } from '@utilities/permission.utilities';
import { PERMISSIONS } from '@/constants/permission-constants';

const PortalBaseUrl = getPortalBaseUrl();

const ShareToken = (token: string) => `ShareToken ${token}`;

export const getFolderGuests = async (key: string): Promise<GuestRecord[] | undefined> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }
    const address = `${PortalBaseUrl}/api/folders/${key}/shares`;

    const response = await fetchData(
        address,
        {
            headers: { Authorization: `Bearer ${accessToken}` }
        },
        true
    );

    // 404 means it is an empty list to return
    if (response.status === 404) {
        return [];
    }

    if (response.ok) {
        return response.json();
    }

    return Promise.reject(response);
};

const calculateExpiryDate = (expiresOn: DropdownItem): string => {
    const created = new Date();
    const expires = new Date(created);

    switch (expiresOn.value) {
        case '7-days':
            expires.setDate(created.getDate() + 7);
            break;
        case '30-days':
            expires.setDate(created.getDate() + 30);
            break;
        case '6-months':
            expires.setMonth(created.getMonth() + 6);
            break;
        case '1-year':
            expires.setFullYear(created.getFullYear() + 1);
            break;
        case '10-years':
            expires.setFullYear(created.getFullYear() + 10);
            break;
        case '15-years':
            expires.setFullYear(created.getFullYear() + 15);
            break;
    }
    return expires.toISOString();
};

const sendInvite = async (email: string, expiresOn: string, folderKey: string) => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const address = `${PortalBaseUrl}/api/folders/${folderKey}/shares`;

    const response = await fetchData(
        address,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
            body: JSON.stringify({ key: folderKey, email, expiresOn })
        },
        true
    );
    return response;
};

export const sendFolderInvites = async (invites: string[], expires: DropdownItem, folderKey: string): Promise<GuestRecord[]> => {
    const results = await lastValueFrom(
        from(invites).pipe(
            map((invite) => ({
                key: `temp___${generateGuid()}`, // this will let the share get added to the UI. When the user rerenders it will have the right key
                email: invite,
                expires: calculateExpiryDate(expires),
                createdOn: new Date().toISOString()
            })),
            mergeMap((i) => from(sendInvite(i.email, i.expires, folderKey)).pipe(map(() => i))),
            toArray() // collect results into an array
        )
    );

    return results as GuestRecord[];
};

// this is from a logged in user who is sharing a folder
export const resendShareLink = async (folderKey: string, shareKey: string) => {
    try {
        const accessToken = await getAccessToken();

        if (!accessToken) {
            return Promise.reject(new Error('Unable to retrieve access token'));
        }
        const address = `${PortalBaseUrl}/api/folders/${folderKey}/shares/${shareKey}/resendemail`;
        const response = await fetchData(address, {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` }
        });

        if (!response.ok) {
            return Promise.reject(response);
        }
    } catch (err) {
        throw new Error(`${err}`);
    }
};

export const revokeFolderAccess = async (folderKey: string, shareKey: string) => {
    try {
        const accessToken = await getAccessToken();

        if (!accessToken) {
            return Promise.reject(new Error('Unable to retrieve access token'));
        }
        const address = `${PortalBaseUrl}/api/folders/${folderKey}/shares/${shareKey}`;
        const response = await fetchData(address, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${accessToken}` }
        });

        if (!response.ok) {
            return Promise.reject(response);
        }
    } catch (err) {
        throw new Error(`${err}`);
    }
};

export interface ValidateShareLinkResponse {
    token_expired: boolean;
    share_expired: boolean;
    share_cancelled: boolean;
}

export const regenerateShareLink = async (token: string) => {
    try {
        const address = `${PortalBaseUrl}/api/shares/regenerate`;
        const response = await fetchData(address, {
            method: 'POST',
            headers: { Authorization: ShareToken(token) }
        });

        return response.ok;
    } catch (err) {
        throw new Error(`${err}`);
    }
};

export const sendLastUpdated = async (token: string) => {
    try {
        const address = `${PortalBaseUrl}/api/shares`;
        const response = await fetchData(address, {
            method: 'PUT',
            headers: { Authorization: ShareToken(token) }
        });

        const status = await response.json();
        return status;
    } catch (err) {
        throw new Error(`${err}`);
    }
};

export const validateShareLink = async (token: string) => {
    try {
        const address = `${PortalBaseUrl}/api/shares/validate`;
        const response = await fetchData(address, {
            method: 'POST',
            headers: { Authorization: ShareToken(token) }
        });

        const { status } = await response.json();

        // the 'Active' state
        const returnPayload = { token_expired: false, share_expired: false, share_cancelled: false };

        if (status === 'TokenExpired') {
            returnPayload.token_expired = true;
        }

        if (status === 'ShareExpired') {
            returnPayload.share_expired = true;
        }

        if (status === 'ShareCancelled') {
            returnPayload.share_cancelled = true;
        }

        return returnPayload;
    } catch (err) {
        throw new Error(`${err}`);
    }
};

export const canUserShareFolder = async (folderKey: string) => {
    const permissions = await getFolderUserPermissions(folderKey);

    if (hasPermission([PERMISSIONS.SHARE_MODELS], permissions)) {
        return true;
    }

    return false;
};
